import { FilterType } from "../../store/emailStore";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { vetsApi, Vet } from "../../api/vets";
import { draftsApi } from "../../api/drafts";
import { useChatStore } from "../../store/chatStore";
import { AgendaWidget } from "../AgendaWidget";
import {
  Inbox,
  AlertTriangle,
  CalendarDays,
  Stethoscope,
  UserX,
  GitMerge,
  Archive,
  FileEdit,
  HeartPulse,
  FlaskConical,
  ChevronRight,
  Bot,
} from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

/**
 * Hook per il draft count globale (condiviso tra tutte le pagine).
 * Refetch ogni 30s per mantenere il count aggiornato.
 */
function useGlobalDraftCount() {
  const { data: drafts } = useQuery({
    queryKey: ["drafts-global"],
    queryFn: () => draftsApi.list(),
    refetchInterval: 30000,
    staleTime: 25000,
  });
  return drafts?.length ?? 0;
}

function VetList({
  onVetClick,
  selectedVetId,
}: {
  onVetClick: (vetId: string | null) => void;
  selectedVetId: string | null;
}) {
  const { data: vets, isLoading } = useQuery<Vet[]>({
    queryKey: ["vets"],
    queryFn: () => vetsApi.list(true),
  });

  return (
    <div className="mt-4 pt-3 border-t border-border">
      <h2 className="text-xs font-semibold text-muted-foreground  mb-2 px-3">
        Veterinari
      </h2>
      <nav className="space-y-0.5" aria-label="Veterinari">
        <button
          onClick={() => onVetClick(null)}
          className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-muted"
        >
          Tutti i veterinari
        </button>
        {isLoading ? (
          <div className="px-3 py-2 text-sm text-muted-foreground">
            Caricamento...
          </div>
        ) : vets && vets.length > 0 ? (
          vets.map((vet) => (
            <button
              key={vet.id}
              onClick={() => onVetClick(vet.id)}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all duration-150 ${
                selectedVetId === vet.id
                  ? "bg-primary text-primary-foreground font-medium"
                  : "text-foreground hover:bg-muted"
              }`}
            >
              <span className="flex items-center gap-2 break-words">
                <Stethoscope className="size-icon-ui shrink-0 text-current" />
                <span className="break-words">{vet.full_name}</span>
              </span>
              {vet.open_cases_count !== undefined &&
                vet.open_cases_count > 0 && (
                  <span className="px-1.5 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                    {vet.open_cases_count}
                  </span>
                )}
            </button>
          ))
        ) : (
          <div className="px-3 py-2 text-sm text-muted-foreground">
            Nessun veterinario
          </div>
        )}
      </nav>
    </div>
  );
}

interface SidebarItem {
  id: FilterType;
  label: string;
  icon: React.ReactNode;
  count?: number;
}

interface SidebarProps {
  selectedFilter?: FilterType;
  onFilterChange?: (filter: FilterType) => void;
  selectedVetId: string | null;
  onVetClick: (vetId: string | null) => void;
  counts?: {
    all: number;
    urgent: number;
    appointments: number;
    clinics: number;
    unassigned: number;
    conflicts: number;
  };
  showFilters?: boolean;
}

export function Sidebar({
  selectedFilter,
  onFilterChange,
  selectedVetId,
  onVetClick,
  counts,
  showFilters = false,
}: SidebarProps) {
  const location = useLocation();
  const draftCount = useGlobalDraftCount();
  const currentPath = location.pathname;
  const { setIsOpen: setChatOpen } = useChatStore();

  const filterItems: SidebarItem[] = [
    {
      id: "all",
      label: "Tutti",
      icon: <Inbox className="size-icon-ui" />,
      count: counts?.all,
    },
    {
      id: "urgent",
      label: "Urgenti",
      icon: <AlertTriangle className="size-icon-ui" />,
      count: counts?.urgent,
    },
    {
      id: "appointments",
      label: "Appuntamenti",
      icon: <CalendarDays className="size-icon-ui" />,
      count: counts?.appointments,
    },
    {
      id: "clinics",
      label: "Cliniche",
      icon: <Stethoscope className="size-icon-ui" />,
      count: counts?.clinics,
    },
    {
      id: "unassigned",
      label: "Da assegnare",
      icon: <UserX className="size-icon-ui" />,
      count: counts?.unassigned,
    },
    {
      id: "conflicts",
      label: "Conflitti",
      icon: <GitMerge className="size-icon-ui" />,
      count: counts?.conflicts,
    },
  ];

  // Static navigation links - always visible, highlight current page
  const navLinks = [
    {
      to: "/triage",
      label: "Triage",
      display: "Conversazioni",
      icon: <Inbox className="size-icon-ui text-current" />,
    },
    {
      to: "/emails",
      label: "Email Archive",
      display: "Archivio",
      icon: <Archive className="size-icon-ui text-current" />,
    },
    {
      to: "/drafts",
      label: "Draft Review",
      display: "Bozze",
      icon: <FileEdit className="size-icon-ui text-current" />,
      count: draftCount,
    },
    {
      to: "/demo",
      label: "Demo casi reali",
      display: "Demo casi reali",
      icon: <FlaskConical className="size-icon-ui text-current" />,
    },
    {
      to: "/system-health",
      label: "Health Check",
      display: "Stato del sistema",
      icon: <HeartPulse className="size-icon-ui text-current" />,
    },
  ];

  return (
    <aside className="mail-sidebar h-full flex flex-col shrink-0 overflow-hidden">
      <ScrollArea className="flex-1 h-0">
        <div className="p-3">
          {/* Azioni header con AI Chat button a destra */}
          <div className="flex items-center justify-between mb-2 px-3">
            <h2 className="text-xs font-semibold text-muted-foreground ">
              Caselle
            </h2>
            <button
              onClick={() => setChatOpen(true)}
              className="relative flex items-center justify-center size-7 rounded-md bg-primary/10 text-primary hover:bg-primary/20 transition-all duration-300 group"
              title="Apri AI Chat"
            >
              <Bot className="size-4 group-hover:scale-110 transition-transform duration-300" />
              {/* Subtle glow animation */}
            </button>
          </div>

          <nav className="space-y-0.5" data-testid="sidebar-actions">
            {navLinks.map((link) => {
              const isActive =
                (link.to === "/triage" &&
                  (currentPath === "/" || currentPath.startsWith("/cases/"))) ||
                currentPath === link.to ||
                (link.to !== "/" && currentPath.startsWith(link.to));
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  aria-current={isActive ? "page" : undefined}
                  data-testid={`link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all duration-150 ${
                    isActive
                      ? "bg-primary text-primary-foreground font-medium"
                      : "text-foreground hover:bg-muted"
                  }`}
                >
                  <span className="flex items-center gap-2.5">
                    <span
                      className={
                        isActive ? "text-primary" : "text-muted-foreground"
                      }
                    >
                      {link.icon}
                    </span>
                    <span>{link.display}</span>
                  </span>
                  {"count" in link && (link as any).count > 0 && (
                    <span
                      className="px-1.5 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground"
                      data-testid="draft-count-badge"
                    >
                      {(link as any).count}
                    </span>
                  )}
                  {!("count" in link) && (
                    <ChevronRight className="size-3.5 text-current" />
                  )}
                </Link>
              );
            })}
          </nav>

          <VetList onVetClick={onVetClick} selectedVetId={selectedVetId} />

          {/* Filtri - solo dove serve (Triage), appaiono sotto veterinari */}
          {showFilters && (
            <div className="mt-4 pt-3 border-t border-border">
              <h2 className="text-xs font-semibold text-muted-foreground  mb-2 px-3">
                Filtri
              </h2>
              <nav className="space-y-0.5" data-testid="sidebar-filters">
                {filterItems.map((item) => (
                  <button
                    key={item.id}
                    data-testid={`filter-${item.id}`}
                    aria-pressed={selectedFilter === item.id}
                    onClick={() => onFilterChange?.(item.id)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm text-foreground hover:bg-muted transition-all duration-150 ${
                      selectedFilter === item.id
                        ? "bg-primary text-primary-foreground font-medium "
                        : ""
                    }`}
                  >
                    <span className="flex items-center gap-2.5">
                      <span className="text-muted-foreground">{item.icon}</span>
                      <span>{item.label}</span>
                    </span>
                    {item.count !== undefined && (
                      <span className="ml-auto px-1.5 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                        {item.count}
                      </span>
                    )}
                  </button>
                ))}
              </nav>
            </div>
          )}

          <details className="mt-4 border-t border-border pt-3">
            <summary className="px-3 text-xs font-semibold text-muted-foreground cursor-pointer">
              Agenda di oggi
            </summary>
            <AgendaWidget />
          </details>
        </div>
      </ScrollArea>
    </aside>
  );
}
